import React, {
  ChangeEventHandler,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styles from './styles/forgotPassword.scss';
import { debounce, validateEmail } from '@shamrock-core/common/utils';
import { TextInput, CheckBox, Button, Spinner } from 'shamrock-clover-ui';
import sentinelService from '../../services/sentinel.service';
import { Variant } from 'shamrock-clover-ui/dist/clover/components/Snackbar/Snackbar';
import { useTranslation } from 'react-i18next';

interface ForgotPasswordProps {
  closeForgotPassword: () => void;
  setShowSnackbar: Dispatch<SetStateAction<boolean>>;
  setSnackbarMessage: Dispatch<SetStateAction<string>>;
  setSnackbarDuration: Dispatch<SetStateAction<number>>;
  setSnackbarVariant: Dispatch<SetStateAction<Variant>>;
}

const ForgotPassword: FC<ForgotPasswordProps> = ({
  closeForgotPassword,
  setShowSnackbar,
  setSnackbarMessage,
  setSnackbarDuration,
  setSnackbarVariant,
}) => {
  const [email, setEmail] = useState<string>('');
  const [sendingLinkLoading, setSendingLinkLoading] = useState(false);
  const [errorText, setErrorText] = useState<string>(null);
  const [sendViaText, setSendViaText] = useState(false);

  const setErrorTextDebounce = useCallback(
    debounce((errorText) => setErrorText(errorText), 1000),
    [],
  );
  const { t } = useTranslation();
  const confirmationViaText = t('a_text_message_has_been_sent_to_your_mobile_with_a_reset_password_link');
  const confirmationViaEmail =
    t('a_reset_password_link_has_been_emailed_if_you_did_not_receive_it_yet_check_spam_folder');

  const isValidEmail = useMemo(() => validateEmail(email.trim()), [email]);

  useEffect(() => setShowSnackbar(false), []);

  useEffect(() => {
    email && !isValidEmail && setErrorTextDebounce('Invalid email address');
    (!email || isValidEmail) && setErrorTextDebounce(null);
  }, [isValidEmail, email]);

  const updateEmail: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  const sendLink = useCallback(async () => {
    try {
      setSendingLinkLoading(true);
      await sentinelService.forgotPassword({ email, sendSms: sendViaText });
      setShowSnackbar(true);
      setSnackbarMessage(sendViaText ? confirmationViaText : confirmationViaEmail);
      setSnackbarVariant('success');
      setSnackbarDuration(10000);
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarMessage(t('an_error_occurred_please_try_again_later'));
      setSnackbarDuration(10000);
    } finally {
      setSendingLinkLoading(false);
    }
  }, [email, sendViaText]);

  return (
    <div className={styles.forgotPassword}>
      <div className={styles.mainContainer}>
        <h3 className={styles.header}>{t('authentication.password_reset_title')}</h3>
        <p>{t('enter_your_email_address_associated_with_your_account_to_reset_your_password')}</p>
        <TextInput
          className={styles.emailInput}
          autoComplete={false}
          onChange={updateEmail}
          variant="filled"
          label={t('email')}
          value={email}
          error={errorText}
        />
        <CheckBox
          onClick={() => setSendViaText(!sendViaText)}
          checked={sendViaText}
          text={t('reset_via_text_message')}
        />
      </div>
      <div className={styles.buttonContainer}>
        <div className={styles.button}>
          <Button data-testid="back-login" buttonStyle="text" onClick={closeForgotPassword} width="145">
            {t('back_to_login')}
          </Button>
        </div>
        <div className={styles.button}>
          <Button disabled={!isValidEmail || sendingLinkLoading} onClick={sendLink} width="145">
            {sendingLinkLoading ? <Spinner color="white" size="25" /> : t('send_link')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
